import type React from 'react';

import { Box, Divider, FormField, Input, Label } from '@zillow/constellation-10';

import OptionSelector from './OptionSelector';
import HeaderToggle from './HeaderToggle';
import type { Section } from '../../../../models';
import { ELECTRICITY } from '../../../../constants';
import MultiFamilyLandlordUtility, { isMultiFamily } from './MultiFamilyLandlordUtility';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function Electricity_v2({ section, setSectionValues }: Props): JSX.Element {
  function setLandlordResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: true,
    });
  }

  function setTenantResponsibility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY]: true,
      [ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA]: '',
      [ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE]: '',
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      [ELECTRICITY.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED),
    });

    section.getField(ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY).setEnteredValueToDefault();
    section.getField(ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY).setEnteredValueToDefault();
    section.getField(ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA).setEnteredValueToDefault();
    section
      .getField(ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE)
      .setEnteredValueToDefault();
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  const electricity_isEnabled = section.getFieldValue(ELECTRICITY.FIELD_NAME.IS_ENABLED);
  const tenantSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.TENANT_RESPONSIBILITY);
  const landlordSelected = section.getFieldValue(ELECTRICITY.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const serviceProvider = section.getFieldValue(ELECTRICITY.FIELD_NAME.SERVICE_PROVIDER);

  return (
    <>
      <HeaderToggle
        header="Electricity"
        subheader="Tenant pays in addition to rent"
        switchOn={electricity_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {electricity_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <Box css={{ marginBottom: 'layout.tight' }}>
            <OptionSelector
              disabled={!electricity_isEnabled}
              optionsText="Who will be responsible for electricity set up?"
              option1Content="Responsible for account set up and direct payments to specified service provider."
              option2Content={
                isMultiFamilyLandlord
                  ? 'Responsible for account set up and will bill tenant separately.'
                  : 'Responsible for account set up and will bill tenant ' + 'a reasonable proportion separately.'
              }
              option1Selected={tenantSelected}
              onOption1Click={setTenantResponsibility}
              option2Selected={landlordSelected}
              onOption2Click={setLandlordResponsibility}
            />
          </Box>

          {tenantSelected && (
            <FormField
              required
              label={<Label id="tenantSelected">What service provider?</Label>}
              control={<Input aria-labelledby="tenantSelected" value={serviceProvider} onChange={setServiceProvider} />}
            />
          )}

          {isMultiFamilyLandlord && landlordSelected && (
            <MultiFamilyLandlordUtility
              section={section}
              utilityName="electricity"
              percentageFieldName={ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.MONTHLY_COST_PERCENTAGE}
              formulaFieldName={ELECTRICITY.FIELD_NAME.MULTIFAMILY_LANDLORD_RESPONSIBILITY.FORMULA}
              setSectionValues={setSectionValues}
            />
          )}
        </>
      )}
    </>
  );
}

Electricity_v2.description = 'Illinois Electricity component';

export default Electricity_v2;
